// Runtime configuration. The REST base is versioned under /api (Caddy routes
// /api/* to the Go server; docs/02 puts REST under /v1). All other build-time
// switches (VAPID key for Web Push, signed endpoint-list refresh source) are
// read here once so the rest of the shell never touches `import.meta.env`.

import { devApiBaseUrl } from './devServer.js';

function env(name: string): string | undefined {
  const value = import.meta.env[name] as string | undefined;
  return value && value.length > 0 ? value : undefined;
}

// A developer-mode override (Settings → Developer) wins over the build-time
// default, so a shipped APK can be pointed at a local/staging backend without
// a rebuild. Read once at import; changing it triggers a reload.
export const API_BASE_URL: string = devApiBaseUrl() ?? env('VITE_API_BASE_URL') ?? '/api/v1';

/**
 * Server VAPID public key (base64url) for Web Push. Empty when the deployment
 * has push disabled — the shell then skips subscription entirely.
 */
export const VAPID_PUBLIC_KEY: string = env('VITE_VAPID_PUBLIC_KEY') ?? '';

/**
 * Optional INDEPENDENT MIRROR of the signed endpoint list (docs/04 §2, third
 * distribution channel: a static copy on GitHub raw / Cloudflare Pages / S3 —
 * different ASNs, high collateral damage to block). It is the only channel that
 * still reaches a client which can no longer reach any of our own endpoints, so
 * it is tried when — and only when — the origin poll cannot be reached at all
 * (`@merkury/brain/runtime/endpointsRefresh.js`, which every platform shares).
 *
 * The URL is untrusted: whatever it serves is adopted only on a valid Ed25519
 * signature against `VITE_ENDPOINTS_PUBKEY` (the baked keyring) and a strictly
 * higher version. Nothing is provisioned today, so this is normally empty and
 * the app runs against its serving origin on the baked-in bootstrap list.
 */
export const ENDPOINTS_MIRROR_URL: string | undefined = env('VITE_ENDPOINTS_MIRROR_URL');
export const ENDPOINTS_PUBKEY: string | undefined = env('VITE_ENDPOINTS_PUBKEY');

/** A bare `host` or `host:port` label — no scheme, no path. */
const BARE_HOSTNAME_RE =
  /^(?=.{1,253}$)[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+(:[0-9]{1,5})?$/;

/**
 * True for a dotted-quad IPv4 literal, with or without a trailing `:port`
 * (e.g. `1.2.3.4`, `1.2.3.4:8443`). `BARE_HOSTNAME_RE` matches these too —
 * digits-only labels are valid DNS labels — so they must be excluded
 * separately: a `kind:'domain'` endpoint (what every mirror becomes, see
 * `endpoints.ts`) has no mandatory pin and relies on the CA/hostname trust
 * chain, which does not apply to a bare IP. `protocol/src/endpoints.ts`
 * requires a literal IP to be `kind:'ip'` with a mandatory `spkiPin` instead —
 * a shape this bare-hostname env var has no way to supply. Rejecting the
 * literal here means a mistyped `VITE_BOOTSTRAP_MIRRORS` can never smuggle an
 * unpinned IP into the bootstrap list or any snapshot/log derived from it.
 */
function isIPv4Literal(host: string): boolean {
  const withoutPort = host.split(':')[0]!;
  const octets = withoutPort.split('.');
  return octets.length === 4 && octets.every((o) => /^\d{1,3}$/.test(o) && Number(o) <= 255);
}

/**
 * Hard cap on how many mirrors `parseBootstrapMirrors` will emit. Signaling
 * races the primary plus every bootstrap entry with a fixed stagger and
 * per-attempt timeout (`RACE_STAGGER_MS` / `ATTEMPT_TIMEOUT_MS`,
 * `brain/src/call/signaling/client.ts`, currently 4s / 10s), so a
 * fully-failing round costs roughly `(entries - 1) * 4s + 10s`. Capping at 3
 * mirrors (4 endpoints total, ~22s worst case) keeps a misconfigured build —
 * one that bakes far more hosts than `ci/mirrors.yml` ever lists — from
 * stretching a failed connection attempt anywhere near the 30s call-setup
 * timeout.
 */
const MAX_BOOTSTRAP_MIRRORS = 3;

/**
 * Parse `VITE_BOOTSTRAP_MIRRORS` into bare hostnames, order preserved,
 * duplicates and IPv4 literals dropped, capped at `MAX_BOOTSTRAP_MIRRORS`.
 * Exported (over inlining into the constant below) so its degrade-on-garbage
 * behavior is unit-testable without stubbing `import.meta.env`. Never
 * throws — an unparseable or empty entry is just skipped, so the worst a
 * broken build config can do is fall back to the single-entry same-origin
 * bootstrap list, never crash boot.
 */
export function parseBootstrapMirrors(raw: string | undefined): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const hosts: string[] = [];
  for (const piece of raw.split(/[,\s]+/)) {
    if (hosts.length >= MAX_BOOTSTRAP_MIRRORS) break;
    const host = piece.trim().toLowerCase();
    if (!host || seen.has(host) || !BARE_HOSTNAME_RE.test(host) || isIPv4Literal(host)) continue;
    seen.add(host);
    hosts.push(host);
  }
  return hosts;
}

/**
 * Mirror hostnames for the baked-in bootstrap endpoint list (`shell/endpoints.ts`
 * `bootstrapEndpointList`), from `ci/mirrors.yml` via the Makefile-baked
 * `VITE_BOOTSTRAP_MIRRORS`. Empty in dev/self-hosted builds, where the
 * single-entry same-origin bootstrap is the only floor.
 */
export const BOOTSTRAP_MIRRORS: readonly string[] = parseBootstrapMirrors(
  env('VITE_BOOTSTRAP_MIRRORS'),
);

/**
 * Debug-build flag, baked in by `make apk-debug` (VITE_DEV_BUILD=1). Off in
 * every production/release build. Gates developer testing shortcuts that must
 * never exist in a shipped release — e.g. hold-to-skip the recovery-phrase
 * confirmation quiz during onboarding.
 */
export const DEV_BUILD: boolean = env('VITE_DEV_BUILD') === '1';

function publicWebOrigin(): string | undefined {
  try {
    const { protocol, hostname, origin } = globalThis.location;
    if (protocol !== 'https:' && protocol !== 'http:') return undefined;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return undefined;
    return origin;
  } catch {
    return undefined;
  }
}

/**
 * Canonical public origin for share links and the "my code" QR
 * (`https://<domain>/add#<uuid>`, docs/06 §2). Inside the Android WebView
 * `location.origin` is `https://localhost` — meaningless to a recipient — so
 * an APK build must bake `VITE_SHARE_ORIGIN`. When neither a baked origin nor
 * a real public web origin exists, this is undefined and the UI falls back to
 * sharing the bare UUID (scanning and pasting accept both forms).
 */
export const SHARE_ORIGIN: string | undefined = env('VITE_SHARE_ORIGIN') ?? publicWebOrigin();
